import {useMemo, useState} from 'react';
import {IntlShape, useIntl} from 'react-intl';
import {
  Card,
  Form,
  Modal,
  OverlayTrigger,
  Tooltip,
  Button as BootstrapButton,
} from 'react-bootstrap';
import axios from 'axios';
import Level3Title from '../../../../../app/common/components/title/Level3Title';
import Button from '../../../../../Components/Button';
import ActionIcon from '../../../../../Components/ActionIcon';
import FilterSelect, {FilterOption} from '../../../../../app/common/components/select/FilterSelect';
import {DateTime} from 'luxon';
import {EntreeBlacklistEntrant} from './mocks';
import {genererPeriodesDisponibles} from './periodesFacturation';
import {
  useAjouterBlacklistEntrant,
  useGetBlacklistEntrants,
  useGetTicketsAppelsEntrants,
  useGetTrunkNumeros,
  useSupprimerBlacklistEntrant,
} from '../../../Hooks/TelephonieManager';
import Loading from '../../../../../app/common/components/Loading';

/**
 * « Voice/LimitationsOutgoing.aspx » (nom trompeur côté legacy : gère en réalité le
 * blacklisting d'appels ENTRANTS, pas sortants). Bloque des numéros appelants précis pour un
 * ou tous les numéros de la prestation, avec 2 canaux d'ajout : saisie manuelle, ou sélection
 * depuis le « décompte détaillé » (historique d'appels).
 */

const TOUS_LES_NUMEROS = '*';

/** Doit rester cohérent avec le libellé DECOMPTE_TITLE, qui annonce 12 mois au client. */
const NOMBRE_MOIS_DECOMPTE = 12;

/** L'API relaie les erreurs de la plateforme voix en RFC 9457 (`detail`, `status`). */
interface ApiProblem {
  detail?: string;
}

const messageErreurEntree = (error: unknown, intl: IntlShape): string => {
  if (axios.isAxiosError(error) && error.response) {
    const detail = (error.response.data as ApiProblem | undefined)?.detail;
    if (detail) {
      return detail;
    }
  }
  return intl.formatMessage({id: 'COMMON.INTERNAL_ERROR'});
};

interface EchecAction {
  label: string;
  message: string;
}

const DecompteDetailleModal = ({
  serviceId,
  numeroSelectionne,
  onFermer,
  onValider,
}: {
  serviceId: number;
  numeroSelectionne: string;
  onFermer: () => void;
  onValider: (numerosAppelants: string[], numeroAppele: string) => void;
}): JSX.Element => {
  const intl = useIntl();
  const periodes = useMemo(() => genererPeriodesDisponibles(intl, NOMBRE_MOIS_DECOMPTE), [intl]);
  const [etape, setEtape] = useState<'periode' | 'tickets' | 'recap'>('periode');
  const [periode, setPeriode] = useState('');
  const [numero, setNumero] = useState(numeroSelectionne);
  const [appelantsSelectionnes, setAppelantsSelectionnes] = useState<Set<string>>(new Set());
  const {data: numerosData} = useGetTrunkNumeros(serviceId);
  const {data: tickets, isLoading: isLoadingTickets} = useGetTicketsAppelsEntrants(
    serviceId,
    periode,
    numero === TOUS_LES_NUMEROS ? undefined : numero,
    etape === 'tickets'
  );

  const periodeOptions: FilterOption<string>[] = periodes.map((p) => ({
    value: p.moisApi,
    label: p.label,
  }));
  const numeroOptions: FilterOption<string>[] = [
    {
      value: TOUS_LES_NUMEROS,
      label: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.TOUS_LES_NUMEROS'}),
    },
    ...(numerosData?.numeros ?? []).map((n) => ({value: n.numero, label: n.numero})),
  ];
  const appelantsUniques = tickets ? Array.from(new Set(tickets.map((t) => t.numeroAppelant))) : [];

  const basculerAppelant = (numeroAppelant: string): void => {
    setAppelantsSelectionnes((precedent) => {
      const suivant = new Set(precedent);
      if (suivant.has(numeroAppelant)) {
        suivant.delete(numeroAppelant);
      } else {
        suivant.add(numeroAppelant);
      }
      return suivant;
    });
  };

  return (
    <Modal show onHide={onFermer} centered size="lg">
      <Modal.Header closeButton>
        <Modal.Title>
          {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ENTRANTS.DECOMPTE_TITLE'})}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {etape === 'periode' && (
          <div className="d-flex flex-column gap-4">
            <div className="d-flex align-items-center gap-3">
              <Form.Label className="fw-bold fs-6 mb-0" style={{minWidth: 90}}>
                {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ENTRANTS.PERIODE'})}
              </Form.Label>
              <FilterSelect
                options={periodeOptions}
                value={periode}
                onChange={setPeriode}
                maxWidth={400}
              />
            </div>
            <div className="d-flex align-items-center gap-3">
              <Form.Label className="fw-bold fs-6 mb-0" style={{minWidth: 90}}>
                {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ENTRANTS.APPELE'})}
              </Form.Label>
              <FilterSelect
                options={numeroOptions}
                value={numero}
                onChange={setNumero}
                maxWidth={400}
              />
            </div>
          </div>
        )}

        {etape === 'tickets' && (
          <div>
            {isLoadingTickets || !tickets ? null : appelantsUniques.length === 0 ? (
              <p className="text-muted fs-6">
                {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ENTRANTS.AUCUNE_COMMUNICATION'})}
              </p>
            ) : (
              <table className="table table-sm">
                <thead>
                  <tr className="table-primary">
                    <th />
                    <th>{intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ENTRANTS.APPELANT'})}</th>
                    <th>{intl.formatMessage({id: 'TELEPHONIE.PBX.HISTORIQUE.COLUMN.DATE'})}</th>
                  </tr>
                </thead>
                <tbody>
                  {appelantsUniques.map((numeroAppelant) => {
                    const dernierAppel = tickets
                      .filter((t) => t.numeroAppelant === numeroAppelant)
                      .sort((a, b) => b.dateHeure.localeCompare(a.dateHeure))[0];
                    return (
                      <tr key={numeroAppelant}>
                        <td>
                          <Form.Check
                            type="checkbox"
                            id={`ticket-${numeroAppelant}`}
                            checked={appelantsSelectionnes.has(numeroAppelant)}
                            onChange={() => basculerAppelant(numeroAppelant)}
                          />
                        </td>
                        <td>{numeroAppelant}</td>
                        <td className="text-muted fs-7">
                          {dernierAppel
                            ? DateTime.fromISO(dernierAppel.dateHeure).toFormat('dd/MM/yyyy HH:mm')
                            : ''}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>
        )}

        {etape === 'recap' && (
          <div>
            <p className="fs-6">
              {intl.formatMessage(
                {
                  id:
                    numero === TOUS_LES_NUMEROS
                      ? 'TELEPHONIE.RESTRICTION.ENTRANTS.CONFIRMATION_TOUS'
                      : 'TELEPHONIE.RESTRICTION.ENTRANTS.CONFIRMATION_NUMERO',
                },
                {numero}
              )}
            </p>
            <ul>
              {Array.from(appelantsSelectionnes).map((numeroAppelant) => (
                <li key={numeroAppelant} className="fs-6 fw-bold">
                  {numeroAppelant}
                </li>
              ))}
            </ul>
          </div>
        )}
      </Modal.Body>
      <Modal.Footer>
        <BootstrapButton variant="danger" onClick={onFermer}>
          {intl.formatMessage({id: 'CANCEL'})}
        </BootstrapButton>
        {etape === 'periode' && (
          <BootstrapButton
            variant="success"
            disabled={!periode}
            onClick={() => setEtape('tickets')}
          >
            {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.CONFIRMER'})}
          </BootstrapButton>
        )}
        {etape === 'tickets' && (
          <BootstrapButton
            variant="success"
            disabled={appelantsSelectionnes.size === 0}
            onClick={() => setEtape('recap')}
          >
            {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ENTRANTS.VALIDER'})}
          </BootstrapButton>
        )}
        {etape === 'recap' && (
          <BootstrapButton
            variant="success"
            onClick={() => onValider(Array.from(appelantsSelectionnes), numero)}
          >
            {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.CONFIRMER'})}
          </BootstrapButton>
        )}
      </Modal.Footer>
    </Modal>
  );
};

const RestrictionAppelsEntrants = ({
  serviceId,
  onConfirm,
  numeroInitial,
}: {
  serviceId: number;
  onConfirm: () => void;
  /** Numéro (format affiché) à présélectionner — sinon « Tous les numéros ». */
  numeroInitial?: string;
}): JSX.Element | null => {
  const intl = useIntl();
  const {data, isLoading} = useGetBlacklistEntrants(serviceId);
  const {data: numerosData} = useGetTrunkNumeros(serviceId);
  const ajouterMutation = useAjouterBlacklistEntrant(serviceId);
  const supprimerMutation = useSupprimerBlacklistEntrant(serviceId);
  const [numeroSelectionne, setNumeroSelectionne] = useState<string>(
    numeroInitial ?? TOUS_LES_NUMEROS
  );
  const [vue, setVue] = useState<'liste' | 'modifier'>('liste');
  const [texteManuel, setTexteManuel] = useState('');
  const [decompteOuvert, setDecompteOuvert] = useState(false);
  const [entreeADissocier, setEntreeADissocier] = useState<EntreeBlacklistEntrant | null>(null);
  const [toutSupprimerOuvert, setToutSupprimerOuvert] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enCours, setEnCours] = useState(false);

  if (isLoading || !data) {
    return <Loading className="col-md-6" />;
  }

  const numeroOptions: FilterOption<string>[] = [
    {
      value: TOUS_LES_NUMEROS,
      label: intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.TOUS_LES_NUMEROS'}),
    },
    ...(numerosData?.numeros ?? []).map((numero) => ({value: numero.numero, label: numero.numero})),
  ];

  const entreesVisibles = data.filter((entree) => entree.numeroAppele === numeroSelectionne);

  /**
   * Ajoute plusieurs appelants et en supprime d'autres en parallèle, sans avaler les échecs
   * partiels : les réussites restent (la query est invalidée par chaque mutation), les échecs
   * sont listés pour que l'utilisateur puisse les corriger. Rien ne déduplique côté serveur, une
   * resoumission des mêmes numéros crée donc de nouvelles entrées plutôt que de rejouer l'échec.
   * `numeroAppele` est explicite : le numéro protégé n'est pas toujours le sélecteur principal
   * (la modale décompte détaillé a le sien, choisi indépendamment).
   */
  const appliquerModifications = async (
    numeroAppele: string,
    aAjouter: string[],
    aSupprimer: {id: number; label: string}[]
  ): Promise<EchecAction[]> => {
    setEnCours(true);
    const [resultatsAjout, resultatsSuppression] = await Promise.all([
      Promise.allSettled(
        aAjouter.map((numero) =>
          ajouterMutation.mutateAsync({numeroAppelant: numero, numeroAppele})
        )
      ),
      Promise.allSettled(aSupprimer.map((entree) => supprimerMutation.mutateAsync(entree.id))),
    ]);
    setEnCours(false);

    const echecs: EchecAction[] = [];
    resultatsAjout.forEach((resultat, index) => {
      if (resultat.status === 'rejected') {
        echecs.push({label: aAjouter[index], message: messageErreurEntree(resultat.reason, intl)});
      }
    });
    resultatsSuppression.forEach((resultat, index) => {
      if (resultat.status === 'rejected') {
        echecs.push({
          label: aSupprimer[index].label,
          message: messageErreurEntree(resultat.reason, intl),
        });
      }
    });
    return echecs;
  };

  const signalerEchecs = (echecs: EchecAction[]): void => {
    if (echecs.length === 0) {
      setErreur(null);
      return;
    }
    setErreur(
      intl.formatMessage(
        {id: 'TELEPHONIE.RESTRICTION.ENTRANTS.ECHEC_PARTIEL'},
        {details: echecs.map((echec) => `${echec.label} (${echec.message})`).join(', ')}
      )
    );
  };

  const ouvrirEdition = (): void => {
    setTexteManuel('');
    setErreur(null);
    setVue('modifier');
  };

  const ajouterNumeros = async (
    numerosAppelants: string[],
    numeroAppeleModal: string
  ): Promise<void> => {
    const echecs = await appliquerModifications(numeroAppeleModal, numerosAppelants, []);
    signalerEchecs(echecs);
    setDecompteOuvert(false);
    if (echecs.length === 0) {
      setVue('liste');
      onConfirm();
    }
  };

  const confirmerAjoutManuel = async (): Promise<void> => {
    const numeros = texteManuel
      .split(/[\n;]/)
      .map((n) => n.trim())
      .filter((n) => n !== '');

    const echecs = await appliquerModifications(numeroSelectionne, numeros, []);
    // Ne laisse dans le textarea que les numéros restés en échec : les numéros déjà
    // enregistrés ne doivent pas être resoumis, rien ne déduplique côté serveur.
    const numerosEnEchec = new Set(echecs.map((echec) => echec.label));
    setTexteManuel(numeros.filter((numero) => numerosEnEchec.has(numero)).join('\n'));
    signalerEchecs(echecs);
    if (echecs.length === 0) {
      setVue('liste');
      onConfirm();
    }
  };

  const confirmerDissociation = async (): Promise<void> => {
    if (!entreeADissocier) {
      return;
    }
    const cible = entreeADissocier;
    setEntreeADissocier(null);
    try {
      await supprimerMutation.mutateAsync(cible.id);
      setErreur(null);
    } catch (error) {
      setErreur(messageErreurEntree(error, intl));
    }
  };

  const confirmerToutSupprimer = async (): Promise<void> => {
    setToutSupprimerOuvert(false);
    const aSupprimer = entreesVisibles.map((entree) => ({
      id: entree.id,
      label: entree.numeroAppelant,
    }));
    const echecs = await appliquerModifications(numeroSelectionne, [], aSupprimer);
    signalerEchecs(echecs);
  };

  return (
    <div className="mt-4">
      <Card className="mb-4">
        <Card.Body>
          <Level3Title title={intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ENTRANTS_TITLE'})} />
          <div className="mt-4" style={{maxWidth: 320}}>
            <FilterSelect
              options={numeroOptions}
              value={numeroSelectionne}
              onChange={(valeur) => {
                setNumeroSelectionne(valeur);
                setVue('liste');
                setErreur(null);
              }}
            />
          </div>
        </Card.Body>
      </Card>

      <Card>
        <Card.Body>
          <div className="fw-bold fs-4 mb-3">
            {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ENTRANTS.SOUS_TITRE'})}
          </div>
          <p className="fs-6 mb-4">
            {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ENTRANTS_DESCRIPTION'})}
          </p>
          <p className="fs-6 mb-3">
            {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ENTRANTS.LISTE_INTRO'})}
          </p>

          {erreur && <div className="fw-bold text-danger mb-3">{erreur}</div>}

          {entreesVisibles.length === 0 ? (
            <div className="bg-primary text-white text-center fw-bold py-3 mb-4">
              {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ENTRANTS.AUCUN_NUMERO'})}
            </div>
          ) : (
            <div className="mb-4">
              <div className="bg-primary text-white text-center fw-bold py-2">
                {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ENTRANTS.APPELANT'})}
              </div>
              <table className="table table-sm align-middle mb-2">
                <tbody>
                  {entreesVisibles.map((entree) => (
                    <tr key={entree.id}>
                      <td>{entree.numeroAppelant}</td>
                      <td className="text-end">
                        <ActionIcon
                          faIcon="bi bi-trash-fill"
                          label={intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.DELETE'})}
                          onClick={() => setEntreeADissocier(entree)}
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              <div className="text-end">
                <span
                  className="text-primary fs-7 cursor-pointer"
                  onClick={() => setToutSupprimerOuvert(true)}
                >
                  {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ENTRANTS.TOUT_SUPPRIMER'})}
                </span>
              </div>
            </div>
          )}

          {vue === 'modifier' && (
            <div className="mb-4">
              <div className="d-flex align-items-center gap-2 fs-6 mb-2">
                {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ENTRANTS.INTERDIRE_TITRE'})}
                <OverlayTrigger
                  placement="top"
                  overlay={
                    <Tooltip>
                      {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ENTRANTS_DESCRIPTION'})}
                    </Tooltip>
                  }
                >
                  <i className="bi bi-question-circle-fill text-primary cursor-pointer" />
                </OverlayTrigger>
              </div>
              <div className="fs-6 mb-1">
                -{' '}
                {intl.formatMessage(
                  {id: 'TELEPHONIE.RESTRICTION.ENTRANTS.DEPUIS_DECOMPTE'},
                  {
                    lien: (
                      <span
                        key="lien-decompte"
                        className="text-primary cursor-pointer"
                        onClick={() => setDecompteOuvert(true)}
                      >
                        {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ENTRANTS.DECOMPTE_LIEN'})}
                      </span>
                    ),
                  }
                )}
              </div>
              <div className="fs-6 mb-3">
                - {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ENTRANTS.EN_SAISISSANT'})}
              </div>
              <Form.Control
                as="textarea"
                rows={5}
                value={texteManuel}
                onChange={(e) => setTexteManuel(e.target.value)}
              />
            </div>
          )}

          <div className="text-center mt-5">
            {vue === 'liste' ? (
              <Button
                label={intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ENTRANTS.AJOUTER'})}
                onClick={ouvrirEdition}
              />
            ) : (
              <BootstrapButton variant="success" disabled={enCours} onClick={confirmerAjoutManuel}>
                {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.CONFIRMER'})}
              </BootstrapButton>
            )}
          </div>
        </Card.Body>
      </Card>

      {decompteOuvert && (
        <DecompteDetailleModal
          serviceId={serviceId}
          numeroSelectionne={numeroSelectionne}
          onFermer={() => setDecompteOuvert(false)}
          onValider={ajouterNumeros}
        />
      )}

      <Modal show={entreeADissocier != null} onHide={() => setEntreeADissocier(null)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill text-warning me-2" />
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.MODAL.CONFIRM_DELETE_TITLE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ENTRANTS.SUPPRIMER_BODY'})}
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={() => setEntreeADissocier(null)}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <BootstrapButton variant="danger" onClick={confirmerDissociation}>
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.ACTION.DELETE'})}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>

      <Modal show={toutSupprimerOuvert} onHide={() => setToutSupprimerOuvert(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>
            <i className="bi bi-exclamation-triangle-fill text-warning me-2" />
            {intl.formatMessage({id: 'TELEPHONIE.ANNUAIRE.MODAL.CONFIRM_DELETE_TITLE'})}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ENTRANTS.TOUT_SUPPRIMER_BODY'})}
        </Modal.Body>
        <Modal.Footer>
          <BootstrapButton variant="secondary" onClick={() => setToutSupprimerOuvert(false)}>
            {intl.formatMessage({id: 'CANCEL'})}
          </BootstrapButton>
          <BootstrapButton variant="danger" onClick={confirmerToutSupprimer}>
            {intl.formatMessage({id: 'TELEPHONIE.RESTRICTION.ENTRANTS.TOUT_SUPPRIMER'})}
          </BootstrapButton>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default RestrictionAppelsEntrants;
